import { useMemo } from "react";
import type { StudioProjectOutputKind } from "@/features/project/lib/studio-project-plan";
import {
  evaluateStudioPublishReadiness,
  withServerDatasourceReadinessProjection,
} from "@/services/project-api";
import { useStudioStore } from "@/store/studio-store";
import {
  selectCockpitAttentionRoute,
  type StudioCockpitActivity,
} from "../cockpit/studio-cockpit-activity";
import { useStudioCockpit } from "../cockpit/use-studio-cockpit";
import { useStudioProjectOutputKind } from "../components/artifact-mode-switcher";
import {
  isEditorSelectionTargetAvailable,
  useStudioSelectionContext,
} from "../selection-context";
import {
  getStudioAuthorityTasks,
  type StudioAuthorityDomain,
} from "../studio-authority-workspaces";
import {
  resolveStudioContextualTaskMenu,
  type StudioContextualBlocker,
  type StudioContextualTaskMenu,
} from "./studio-contextual-task-menu";

/**
 * React adapter over the pure `resolveStudioContextualTaskMenu` resolver
 * (Task 1). This hook is a read-only projection: it gathers the current
 * selection, catalog, authoritative blocker, and publish-readiness facts
 * from the existing stores/hooks and hands them to the resolver. It never
 * mutates state, never approves anything, and introduces no new store or
 * state machine.
 */
export interface UseStudioContextualTasksInput {
  domain: StudioAuthorityDomain;
}

/**
 * Fallback task id for an authoritative blocker whose own destination
 * doesn't live inside the domain currently being resolved. Validation
 * failures always surface on the insights "quality" surface (see
 * `selectStudioCockpitActivities`'s `STATUS_QUALITY` destination), but the
 * fix belongs on the calling domain's own check/release-shaped task.
 */
function closestCheckReleaseTaskId(
  domain: StudioAuthorityDomain,
  outputKind: StudioProjectOutputKind,
): string {
  if (domain === "design") return "compare";
  if (domain === "guide") return "publish";
  if (domain === "insights") return "release";
  if (outputKind === "game") return "playtest";
  if (outputKind === "document") return "export";
  return "test";
}

/**
 * Recognizes an authoritative blocker from the existing cockpit activity
 * model (`useStudioCockpit`) instead of standing up a second error
 * selector. Only "blocked" activities sourced from validation are treated
 * as contextual-task blockers — other blocked sources (webmcp/chat/manual)
 * are surfaced by the cockpit attention banner elsewhere and are out of
 * scope for task-menu ranking.
 *
 * There is deliberately no publication lane here. This branch used to also
 * admit `source: "publish"` and build a `kind: "publication"` blocker, but
 * `selectStudioCockpitActivities` — the sole producer of these activities —
 * never emitted that source, so the publication blocker was unreachable in
 * production and this comment advertised a second lane that did not exist.
 * The honest publish signal Studio holds is `evaluateStudioPublishReadiness`
 * (read below as `publishGate`), and it is a READINESS estimate:
 * `publishReady: false` is the normal state of every in-progress project.
 * Raising it here as a blocker would put a permanent attention badge on
 * essentially every project. A blocker means a publish was attempted and
 * refused; Studio persists no such fact today — `publishCurrentProject`
 * (store/project-slice.ts) reports its refusal through a transient toast and
 * a rethrow, and `usePublicationRelease().error` is component-local state the
 * cockpit selector (which reads only `useStudioStore`) cannot see. Re-open
 * this lane only once such a fact is actually held in the store, never off
 * the readiness gate.
 */
function deriveContextualBlocker(
  activities: readonly StudioCockpitActivity[],
  domain: StudioAuthorityDomain,
  outputKind: StudioProjectOutputKind,
): StudioContextualBlocker | null {
  const attention = selectCockpitAttentionRoute({
    kind: "idle",
    unreadCount: 0,
    activities: [...activities],
  });
  if (!attention || attention.kind !== "blocked") return null;
  if (attention.source !== "validation") return null;
  const taskId =
    attention.destination.domain === domain
      ? attention.destination.authorityView
      : closestCheckReleaseTaskId(domain, outputKind);
  return {
    kind: "validation",
    message: attention.title,
    taskId,
  };
}

export function useStudioContextualTasks(
  input: UseStudioContextualTasksInput,
): StudioContextualTaskMenu {
  const { domain } = input;
  const outputKind = useStudioProjectOutputKind();
  const selection = useStudioSelectionContext();
  const cockpit = useStudioCockpit();
  const currentProjectId = useStudioStore((s) => s.currentProjectId);
  const currentProject = useStudioStore((s) => s.currentProject);
  const projectMeta = useStudioStore((s) => s.projectMeta);
  const projectPlan = useStudioStore((s) => s.projectPlan);

  const tasks = useMemo(
    () => getStudioAuthorityTasks(domain, outputKind),
    [domain, outputKind],
  );

  const blocker = useMemo(
    () => deriveContextualBlocker(cockpit.activities, domain, outputKind),
    [cockpit.activities, domain, outputKind],
  );

  // Same source of truth as TopBar's publish control and the Publish/Deploy
  // surfaces (services/project-api.ts `evaluateStudioPublishReadiness`) —
  // never inferred from strings, and false whenever no project (and
  // therefore no gate evidence) is open.
  const publishGate = currentProjectId
    ? evaluateStudioPublishReadiness(
        withServerDatasourceReadinessProjection(
          currentProject,
          projectMeta,
          currentProjectId,
        ) as never,
        projectPlan ?? projectMeta?.studioProjectPlan ?? null,
        currentProjectId,
      )
    : null;
  const publishReady = publishGate?.publishReady === true;

  const targetAvailable = useMemo(
    () => isEditorSelectionTargetAvailable(selection),
    [selection],
  );

  return useMemo(
    () =>
      resolveStudioContextualTaskMenu({
        domain,
        outputKind,
        tasks,
        selection,
        blocker,
        publishReady,
        targetAvailable,
      }),
    [domain, outputKind, tasks, selection, blocker, publishReady, targetAvailable],
  );
}
